import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Loader2, Calculator, ArrowRight, Sparkles } from "lucide-react";

export default function QuickCalculator() {
  const [meta, setMeta] = useState({ building_types: [], finish_levels: [], regions: [], countries: [] });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    building_type: "single_family_home",
    square_footage: 2000,
    finish_level: "standard",
    region: "National Average",
    country: "US",
    area_unit: "sqft",
    city: "National average",
  });

  useEffect(() => {
    api.get("/building-types").then(({ data }) => {
      setMeta(data);
      setForm((f) => ({ ...f, building_type: data.building_types[0]?.id || f.building_type }));
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onCountry = (code) => {
    const c = meta.countries.find((x) => x.code === code);
    const unit = c?.unit === "metric" ? "sqm" : "sqft";
    setForm((f) => ({
      ...f,
      country: code,
      area_unit: unit,
      city: "National average",
      square_footage: unit === "sqm" ? 185 : 2000,
    }));
    setResult(null);
  };

  const calc = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/public/quick-estimate", {
        building_type: form.building_type,
        square_footage: Number(form.square_footage) || 10,
        finish_level: form.finish_level,
        region: form.region,
        country: form.country,
        area_unit: form.area_unit,
        city: form.city,
      });
      setResult(data.summary);
    } catch {
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const cityList = (meta.cities && meta.cities[form.country]) || [];

  const sym = result?.currency_symbol || "$";
  const unitWord = form.area_unit === "sqm" ? "sq m" : "sq ft";
  const sliderMax = form.area_unit === "sqm" ? 1800 : 20000;
  const sliderMin = form.area_unit === "sqm" ? 40 : 400;
  const sliderStep = form.area_unit === "sqm" ? 10 : 100;

  const field = "mt-1.5 rounded-sm border-border bg-transparent focus:border-primary focus:ring-primary";
  const lbl = "font-mono2 text-xs uppercase tracking-widest text-muted-foreground";

  return (
    <div className="grid gap-0 border border-border md:grid-cols-2" data-testid="quick-calculator">
      {/* Inputs */}
      <div className="border-b border-border p-8 md:border-b-0 md:border-r">
        <div className="mb-6 flex items-center gap-2">
          <Calculator className="h-4 w-4 text-primary" />
          <span className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">Instant ballpark calculator</span>
        </div>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={lbl}>Country</Label>
              <Select value={form.country} onValueChange={onCountry}>
                <SelectTrigger data-testid="calc-country" className={field}><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {meta.countries.map((c) => (
                    <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className={lbl}>Building type</Label>
              <Select value={form.building_type} onValueChange={(v) => set("building_type", v)}>
                <SelectTrigger data-testid="calc-type" className={field}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.building_types.map((b) => (
                    <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="flex items-end justify-between">
              <Label className={lbl}>Size ({unitWord})</Label>
              <span className="font-mono2 text-sm font-bold text-primary">{Number(form.square_footage).toLocaleString()} {unitWord}</span>
            </div>
            <Slider
              data-testid="calc-sqft-slider"
              className="mt-3"
              min={sliderMin} max={sliderMax} step={sliderStep}
              value={[Number(form.square_footage)]}
              onValueChange={([v]) => set("square_footage", v)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={lbl}>Finish level</Label>
              <Select value={form.finish_level} onValueChange={(v) => set("finish_level", v)}>
                <SelectTrigger data-testid="calc-finish" className={field}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {meta.finish_levels.map((f) => (
                    <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.country === "US" ? (
              <div>
                <Label className={lbl}>Region</Label>
                <Select value={form.region} onValueChange={(v) => set("region", v)}>
                  <SelectTrigger data-testid="calc-region" className={field}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {meta.regions.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className={lbl}>Units</Label>
                <Select value={form.area_unit} onValueChange={(v) => set("area_unit", v)}>
                  <SelectTrigger data-testid="calc-unit" className={field}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sqm">Square metres</SelectItem>
                    <SelectItem value="sqft">Square feet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {cityList.length > 1 && (
            <div>
              <Label className={lbl}>City</Label>
              <Select value={form.city} onValueChange={(v) => { set("city", v); setResult(null); }}>
                <SelectTrigger data-testid="calc-city" className={field}><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {cityList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}


          <Button onClick={calc} disabled={loading} data-testid="calc-run-btn"
            className="w-full rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
            {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Calculating…</> : <><Calculator className="mr-2 h-4 w-4" /> Estimate Cost</>}
          </Button>
        </div>
      </div>

      {/* Result */}
      <div className="relative flex flex-col justify-center bg-secondary/10 p-8">
        <div className="absolute inset-0 bp-grid-fine opacity-40" />
        <div className="relative">
          <AnimatePresence mode="wait">
            {result ? (
              <motion.div key="res" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="calc-result">
                <p className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Estimated total cost · {result.currency}</p>
                <div className="mt-1 font-heading text-5xl font-extrabold tracking-tighter text-primary" data-testid="calc-total">
                  {money(result.total_cost, sym)}
                </div>
                <div className="mt-6 grid grid-cols-2 gap-px border border-border bg-border">
                  {[
                    ["Materials", money(result.material_cost, sym)],
                    ["Labor", money(result.labor_cost, sym)],
                    [`Per ${result.unit_label || unitWord}`, money(result.cost_per_unit ?? result.cost_per_sqft, sym)],
                    ["Timeline", `${result.timeline_weeks} wks`],
                  ].map(([l, v]) => (
                    <div key={l} className="bg-background p-4">
                      <div className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div>
                      <div className="mt-1 font-mono2 text-lg font-bold">{v}</div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 font-mono2 text-[11px] uppercase tracking-widest text-muted-foreground">
                  Ballpark only · {result.currency} · local market
                </p>
                {result.fx_source === "live" && (
                  <p className="mt-1 flex items-center gap-1.5 font-mono2 text-[11px] uppercase tracking-widest text-primary" data-testid="calc-fx-badge">
                    <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" /> Live rates · updated {new Date((result.fx_updated || 0) * 1000).toLocaleDateString()}
                  </p>
                )}
                <Link to="/register" className="mt-5 inline-block" data-testid="calc-cta">
                  <Button variant="outline" className="rounded-sm border-primary font-mono2 uppercase tracking-widest text-primary hover:bg-primary hover:text-black">
                    <Sparkles className="mr-2 h-4 w-4" /> Get full AI scope breakdown <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>
            ) : (
              <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="text-center">
                <Calculator className="mx-auto h-10 w-10 text-muted-foreground/40" />
                <p className="mt-4 max-w-xs font-heading text-lg text-muted-foreground">
                  Pick your country and build, then hit <span className="text-primary">Estimate Cost</span> for an instant local-currency number.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
